import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClearFilter } from '../../../../../assets/icons/filter-close.svg';
import { useAppBranding } from '../../../../../config/branding';
import i18n from '../../../../../i18n';
import { useAppSelector } from '../../../../../redux/hooks';
import BatteryLoader from '../../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../../shared-components/button/Button';
import CustomDatePicker from '../../../../../shared-components/custom-date-range/CustomDatePicker';
import SelectField from '../../../../../shared-components/select-field/SelectField';
import endConsumerApi from '../../../../../utils/api/end-consumer-v2';

/**
 * WalletHistory component
 * @returns WalletHistory component
 */
function WalletHistory() {
  const branding = useAppBranding();
  const { t } = useTranslation('endConsumer');
  const { selectedEndConsumer } = useAppSelector((state) => state.endConsumerType);
  const [walletHistory, setWalletHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [transactionType, setTransactionType] = useState('allTransactions');

  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: '',
  });

  /**
   * Function to remove filter from the list of selected filters
   */
  const handleClearFilters = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Fetch wallet history with filters
   */
  const fetchHistory = useCallback(
    async (userCustomId: string) => {
      setLoading(true);
      try {
        const query: any = {};
        if (transactionType !== 'allTransactions') {
          query.type = transactionType;
        }
        if (dateRange.startDate && dateRange.endDate) {
          query.startDate = dayjs(dateRange.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
          query.endDate = dayjs(dateRange.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }

        const response = await endConsumerApi.fetchWalletHistory(userCustomId, query);
        setWalletHistory(response?.data ?? []);
      } catch (error) {
        console.error('Failed to fetch wallet history:', error);
      } finally {
        setLoading(false);
      }
    },
    [transactionType, dateRange],
  );

  useEffect(() => {
    const { userCustomId } = selectedEndConsumer || {};

    if (!userCustomId) return;

    if (!dateRange.startDate && !dateRange.endDate) {
      fetchHistory(userCustomId);
      return;
    }
    if (dateRange.startDate && dateRange.endDate) {
      fetchHistory(userCustomId);
    }
  }, [selectedEndConsumer?.userCustomId, dateRange.startDate, dateRange.endDate, fetchHistory]);

  /**
   * Handle transaction filter change
   * @param name - Name of the field
   * @param value - Selected value
   */
  const handleTransactionFilter = (name: string, value: any) => {
    setTransactionType(value);
  };

  /**
   * Handle date range change
   * @param name - Name of the field
   * @param value - Selected value
   */
  const handleRangeChange = (name: string, value: string) => {
    setDateRange((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const transactionOptions = [
    { label: t('allTransactions'), value: 'allTransactions' },
    { label: t('credited'), value: 'credit' },
    { label: t('debited'), value: 'debit' },
  ];

  if (loading) {
    return (
      <div className="wallet-history-wrap-loading">
        {' '}
        <BatteryLoader />
      </div>
    );
  }

  return (
    <div className="wallet-history-wrap" dir={i18n.dir()}>
      <div className="wallet-history-top">
        <div className="select-field">
          <SelectField
            name="transactionType"
            options={transactionOptions}
            selectedValue={transactionType}
            onChangeValue={handleTransactionFilter}
          />
        </div>

        <div className="wallet-history-date-filter">
          <CustomDatePicker
            name="startDate"
            value={dateRange.startDate}
            onChangeValue={handleRangeChange}
            placeholder={t('dd/mm/yyyy')}
          />
          <CustomDatePicker
            name="endDate"
            value={dateRange.endDate}
            onChangeValue={handleRangeChange}
            placeholder={t('dd/mm/yyyy')}
          />
        </div>
        <div className="activity-clear-filter">
          <p> </p>
          <MyButton
            label=""
            buttonType="none"
            onClickFunc={handleClearFilters}
            svgIcon={<ClearFilter />}
            hoverLabel={t('clearFilter')}
          />
        </div>
      </div>
      {walletHistory.length === 0 ? (
        <div className="wallet-history-empty">{t('noTransactionsFound')}</div>
      ) : (
        walletHistory.map((group) => (
          <div key={group._id.date} className="wallet-history-content">
            <p className="wallet-history-date">{dayjs(group._id.date).format(t('dd/mm/yyyy'))}</p>
            <div className="wallet-history-item-wrap">
              {group.transactions.map((item: any) => {
                const isDebit = item.type === 'debit';
                let stationName =
                  item.chargingStationDetails?.name ||
                  (isDebit ? t('evChargingSession') : t('walletTopUp'));

                if (item.rentalSession) {
                  stationName = t('powerBankRentalSession');
                }
                const time = dayjs(item.createdAt).format('hh:mm A');

                return (
                  <div key={item._id} className="wallet-history-item">
                    <div className="wallet-history-item-left">
                      <p className="label">
                        {stationName} <span className="value">{time}</span>
                      </p>
                    </div>
                    {item.chargingTransactionId && (
                      <div className="wallet-history-item-middle">
                        <p className="label">
                          {t('transactionId')}{' '}
                          <span className="value">{item.chargingTransactionId}</span>
                        </p>
                      </div>
                    )}
                    <p className={`transaction-amount ${isDebit ? 'debited' : 'credited'}`}>
                      {isDebit ? '-' : '+'}
                      {Number(item.amount).toFixed(2)}{' '}
                      <branding.icons.currencyIcon className="currency-icon" />
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default WalletHistory;
